import type { ComponentType, CSSProperties } from "react";
import type { AppId } from "../data/apps";
import type { WindowParams } from "./window";

/**
 * Widget-IDs - basierend auf App-IDs + System-Widgets
 */
export type WidgetId =
  // System-Widgets (keine App-Zuordnung)
  | "calendar"
  | "clock"
  | "notifications"
  | "voice-agent"
  | "url-linked"
  // App-basierte Widgets
  | "app-store"
  | "settings"
  | "admin"
  | "prompt-library"
  | "pdf-viewer"
  | "multimedia";

/**
 * Widget-Größen - dynamisch anpassbar durch User
 * Format: Höhe x Breite im 2-Spalten-Grid
 */
export type WidgetSize = "small" | "medium" | "tall" | "large";

/**
 * Widget-Kategorie
 */
export type WidgetCategory = "system" | "apps" | "productivity" | "admin";

/**
 * Widget-Definition (ähnlich zu AppDef)
 */
export interface WidgetDef {
  id: WidgetId;
  Icon: ComponentType<{ className?: string; style?: CSSProperties }>;
  defaultSize: WidgetSize;
  category: WidgetCategory;

  // Optional: Verknüpfung zu einer App
  appId?: AppId;

  // System-Widgets sind immer verfügbar
  system?: boolean;

  // Nur für Admins sichtbar
  adminOnly?: boolean;

  // Kann gepinnt werden
  pinnable?: boolean;

  // Hat spezifische Einstellungen
  hasSettings?: boolean;

  // Erlaubte Größen (wenn nicht angegeben, sind alle Größen erlaubt)
  allowedSizes?: WidgetSize[];
}

/**
 * Desktop-Position für Widgets
 */
export interface WidgetDesktopPosition {
  row: number;
  col: number;
}

/**
 * Widget-Konfiguration pro User (persistiert)
 */
export interface WidgetConfig {
  id: WidgetId;
  enabled: boolean;
  size: WidgetSize;
  position: number; // Sortierungs-Index in Sidebar
  pinned: boolean;

  // Desktop-Platzierung (Apple-Style)
  pinnedToDesktop?: boolean;
  desktopPosition?: WidgetDesktopPosition;

  // Widget-spezifische Einstellungen (optional)
  settings?: WidgetSettings;
}

/**
 * Widget-spezifische Einstellungen
 */
export interface UrlLinkItem {
  id: string;
  title: string;
  url: string;
}

export interface WidgetSettings {
  [key: string]: string | number | boolean | undefined | UrlLinkItem[];
  links?: UrlLinkItem[];

  // Beispiele:
  // maxItems?: number // Für Listen-Widgets (Drive, AI Chat, etc.)
  // showPreview?: boolean // Für Content-Widgets
  // autoRefresh?: boolean // Für Live-Widgets
}

/**
 * URL Link Item für URL-Widget
 */
export interface UrlLinkItem {
  id: string; // UUID für jeden Link
  url: string; // HTTPS URL
  title: string; // Nutzer-definierter Titel
  favicon?: string; // Gecachte Favicon-URL (optional)
}

/**
 * Props die jede Widget-Komponente erhält
 */
export interface WidgetProps {
  // Widget-Konfiguration
  config: WidgetConfig;

  // Callback zum Öffnen der zugehörigen App
  onOpenApp: (appId?: AppId, params?: WindowParams) => void;

  // Callback zum Aktualisieren der Widget-Settings
  onUpdateSettings?: (settings: WidgetSettings) => void;

  // Ob Widget im Edit-Modus ist (z.B. im Widget Store)
  editMode?: boolean;

  // Statisches Preview ohne Layout-Animationen (z.B. Drag-Overlay)
  staticPreview?: boolean;
}

/**
 * Widget-Größen als Inline-Styles für Grid (sicherer als CSS-Klassen)
 * Format: Höhe x Breite
 */
export const WIDGET_SIZE_STYLES: Record<WidgetSize, CSSProperties> = {
  small: { gridColumn: "span 1", gridRow: "span 1" }, // 1x1
  medium: { gridColumn: "span 2", gridRow: "span 1" }, // 1x2
  tall: { gridColumn: "span 1", gridRow: "span 2" }, // 2x1
  large: { gridColumn: "span 2", gridRow: "span 2" }, // 2x2
};

/**
 * Widget-Größe in Grid-Zellen (Spalten x Zeilen)
 * Für Desktop-Grid-Platzierung
 */
export const WIDGET_GRID_SIZE: Record<
  WidgetSize,
  { cols: number; rows: number }
> = {
  small: { cols: 2, rows: 2 }, // 2x2 Icon-Zellen (aehnlich Apple "Small")
  medium: { cols: 4, rows: 2 }, // 4x2 Zellen (Wide)
  tall: { cols: 2, rows: 4 }, // 2x4 Zellen (High)
  large: { cols: 4, rows: 4 }, // 4x4 Zellen (Large)
};
